import type { ApiClient } from "@shell/auth/apiClient";
import { describeApiError } from "../../common/errors";
import { searchUsers } from "../../users/api";
import { resolveUserAssignmentToken } from "../../users/resolveUserToken";
import type { ParseAssetsCsvResult } from "./parseAssetsCsv";

/**
 * Rewrites `assignedToUserId` from a name or email into the HR user id
 * before the import is posted. Tokens the directory does not recognize
 * stay as typed so a file that already has user ids still imports.
 */
export async function applyUserTokens(
    client: ApiClient,
    parse: ParseAssetsCsvResult,
): Promise<ParseAssetsCsvResult> {
    if (parse.fatalError !== null) return parse;

    const tokens = [
        ...new Set(
            parse.rows
                .map((row) => row.payload.assignedToUserId)
                .filter((id): id is string => id !== null && id !== ""),
        ),
    ];
    if (tokens.length === 0) return parse;

    const resolved = new Map<string, { readonly id: string } | { readonly error: string }>();
    await Promise.all(
        tokens.map(async (token) => {
            try {
                const results = await searchUsers(client, token);
                resolved.set(token, resolveUserAssignmentToken(token, results));
            } catch (error) {
                resolved.set(token, { error: describeApiError(error) });
            }
        }),
    );

    let hasErrors = parse.hasErrors;
    const rows = parse.rows.map((row) => {
        const token = row.payload.assignedToUserId;
        if (token === null) return row;
        const outcome = resolved.get(token);
        if (outcome === undefined) return row;
        if ("error" in outcome) {
            hasErrors = true;
            return { ...row, errors: [...row.errors, outcome.error] };
        }
        if (outcome.id === token) return row;
        return {
            ...row,
            payload: { ...row.payload, assignedToUserId: outcome.id },
        };
    });

    return { ...parse, rows, hasErrors };
}
